/**
 * @lines98/client/services/game-heartbeat — POST /game/heartbeat wrapper (RP-5 /
 * run-persistence-lifecycle.md T-B §5.3).
 *
 * Slides the server-side TTL of the active all-time session so a long-running run
 * («Pro играет доску днями») never expires while it's played. Best-effort: a network
 * failure is benign (the next ping retries; the session lives on the server clock).
 * A 404 (`session_not_found`) means the session is unknown / consumed / EXPIRED — the
 * caller surfaces «партия истекла», clears the cache, and goes to the lobby (AC-B.6).
 *
 * Body is ONLY `{ session_id }` — anti-cheat-neutral (touches only `expires_at`).
 *
 * Cross-references:
 *   - run-persistence-lifecycle.md §10 (heartbeat contract).
 *   - packages/server/src/routes/game-heartbeat.ts (server contract).
 *   - lib/game-session-heartbeat.ts (RP-5 client orchestration — throttle + expiry).
 */

import { apiClient, ApiClientError } from './api-client.js';

/** Discriminated result of a heartbeat ping. */
export type HeartbeatResult =
  | { readonly status: 'ok'; readonly expiresAt: number }
  /** Session unknown / consumed / EXPIRED (404) — the run is gone (AC-B.6). */
  | { readonly status: 'expired' }
  /** Transient network failure — benign; the next ping retries. */
  | { readonly status: 'network' };

interface HeartbeatResponseBody {
  ok: boolean;
  expires_at?: number;
}

/**
 * Ping `POST /game/heartbeat { session_id }`. Single attempt (no retry — a heartbeat
 * is fire-and-forget liveness; the next save/resume re-pings).
 */
export async function pingHeartbeat(sessionId: string): Promise<HeartbeatResult> {
  let response;
  try {
    response = await apiClient.post<HeartbeatResponseBody>('/game/heartbeat', {
      session_id: sessionId,
    });
  } catch (err) {
    if (err instanceof ApiClientError) return { status: 'network' };
    return { status: 'network' };
  }

  if (response.ok && response.body && typeof response.body.expires_at === 'number') {
    return { status: 'ok', expiresAt: response.body.expires_at };
  }
  // 404 → session gone (unknown / consumed / expired). Anything else non-ok is treated
  // as transient (benign — the next ping retries) so we don't falsely strand the run.
  if (response.status === 404) return { status: 'expired' };
  return { status: 'network' };
}
